import { http } from './http.js';

/** Mirrors the backend's PLATFORM_VALUES (backend/src/middleware/schemas.ts), which the caption request is validated against. */
export type AiPlatform = 'instagram' | 'tiktok' | 'youtube' | 'twitter' | 'facebook' | 'onlyfans';
export type AiCaptionTone = 'casual' | 'professional' | 'playful' | 'inspirational' | 'minimal';

export const AI_CAPTION_TONES: ReadonlyArray<{ value: AiCaptionTone; label: string }> = [
  { value: 'casual', label: 'Casual' },
  { value: 'professional', label: 'Professional' },
  { value: 'playful', label: 'Playful' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'minimal', label: 'Minimal' },
];

/** The server's boot-time check of the provider's credentials and model id. */
export type AiProviderCheck =
  | { ok: true; model: string; checkedAt: string }
  | { ok: false; code: string; message: string; checkedAt: string };

/** GET /api/v1/ai/status */
export interface AiStatus {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  /** null until the server has checked, or when the provider has no check. */
  providerCheck: AiProviderCheck | null;
  sendsMedia: boolean;
  dailyLimit: number;
  usedToday: number;
  remainingToday: number;
  /** ISO time the UTC day, and the quota, rolls over. */
  resetsAt: string;
}

/** POST /api/v1/ai/captions body. The org and user come from the session. */
export interface GenerateAiCaptionInput {
  platform: AiPlatform;
  assetId?: string;
  brief?: string;
  tone?: AiCaptionTone;
  language?: string;
  hashtagCount?: number;
}

export interface AiCaptionResult {
  generationId: string;
  caption: string;
  hashtags: string[];
  altText: string;
  model: string;
  usage: { inputTokens: number; outputTokens: number };
}

/** POST /api/v1/ai/post-drafts body: one post for one creator from a prompt. */
export interface DraftAiPostInput {
  creatorId: string;
  platform: AiPlatform;
  prompt: string;
  tone?: AiCaptionTone;
  hashtagCount?: number;
}

export interface AiPostDraft {
  generationId: string;
  /** The media the AI chose from the creator's own and shared files; null when none fits. */
  assetId: string | null;
  /** Why it chose it, for the person reviewing. Not posted. */
  reason: string;
  caption: string;
  hashtags: string[];
  altText: string;
  model: string;
  /** How many files it chose from. */
  candidates: number;
}

export async function getAiStatus(): Promise<AiStatus> {
  const { data } = await http.get<{ success: boolean; status: AiStatus }>('/v1/ai/status');
  return data.status;
}

export async function generateAiCaption(input: GenerateAiCaptionInput): Promise<AiCaptionResult> {
  const { data } = await http.post<{ success: boolean } & AiCaptionResult>('/v1/ai/captions', input);
  return data;
}

export async function draftAiPost(input: DraftAiPostInput): Promise<AiPostDraft> {
  const { data } = await http.post<{ success: boolean } & AiPostDraft>('/v1/ai/post-drafts', input);
  return data;
}
